import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../api/api';
import TopNav from '../components/TopNav';
import ConfirmModal from '../components/ConfirmModal';
import AlertModal from '../components/AlertModal';
import InvoicePrintEmailActions from '../components/InvoicePrintEmailActions';
import LocationStockEmailActions from '../components/LocationStockEmailActions';
import EmailActions from '../components/EmailActions';
import { useAuth } from '../context/AuthContext';
import { can } from '../accessControl';
import { findBusinessAddress } from '../utils/businessAddress';
import { formatCalendarDate } from '../utils/dateDisplay';
import './css/InventoryPage.css';
import './css/InvoicesPage.css';
import './css/TransfersPage.css';

const emptyLocation = {
  name: '',
  address1: '',
  address2: '',
  address3: '',
  city: '',
  state: '',
  zip: '',
  country: '',
  phone: '',
  email: '',
  website: '',
  instagram: '',
  facebook: '',
  customPlatform: '',
  customAddress: '',
  notes: '',
};

const locationSaveFields = [
  'name',
  'address1',
  'address2',
  'address3',
  'city',
  'state',
  'zip',
  'country',
  'phone',
  'email',
  'website',
  'instagram',
  'facebook',
  'customPlatform',
  'customAddress',
  'active',
  'available',
  'show_on_item_list',
  'notes',
];

const locationValue = (value) => (value === null || value === undefined ? '' : String(value));

const buildLocationUpdatePayload = (currentLocation = {}, editedLocation = {}) => (
  locationSaveFields.reduce((payload, field) => {
    const currentValue = locationValue(currentLocation[field]);
    const editedValue = locationValue(editedLocation[field]);
    if (currentValue !== editedValue) {
      payload[field] = editedLocation[field] ?? '';
    }
    return payload;
  }, {})
);

const money = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '—';
  return number.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
};

const formatDate = (value) => {
  if (!value) return '-';
  return formatCalendarDate(value) || '-';
};

const formatNumber = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '';
  return Number.isInteger(number) ? String(number) : number.toFixed(2);
};

const serialListText = (item) => {
  const serials = item?.serials?.length ? item.serials : [item?.serial].filter(Boolean);
  return serials.join(', ');
};

const stockSerialsText = (row) => (
  Array.isArray(row?.serials) && row.serials.length ? row.serials.join(', ') : '-'
);

const invoiceAmount = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '';
  return number.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: number % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
};

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const AddressBlock = ({ title, address }) => (
  <section className="invoice-address">
    <div className="invoice-address-title">{title}:</div>
    <div className="invoice-address-body">
      <strong>{address?.name}</strong>
      {address?.addr1 && <span>{address.addr1}</span>}
      {(address?.city || address?.state || address?.zip) && (
        <span className="invoice-city-line">
          <span>{address.city}</span>
          <span>{address.state}</span>
          <span>{address.zip}</span>
        </span>
      )}
      {address?.phone && <span>{address.phone}</span>}
      {address?.website && <span>{address.website}</span>}
      {address?.email && <span>{address.email}</span>}
    </div>
  </section>
);

const InvoicePreview = ({ invoice }) => {
  const totalQty = (invoice.items || []).reduce(
    (sum, item) => sum + (Number(item.quantity) || 0),
    0
  );
  const totalPrice = (invoice.items || []).reduce(
    (sum, item) => sum + ((Number(item.quantity) || 0) * (Number(item.price) || 0)),
    0
  );

  return (
    <article className="invoice-sheet invoice-sheet-modal">
      <header className="invoice-header">
        <h1>INVOICE</h1>
        <div className="invoice-brand">
          <div>IRENE'S</div>
          <span>nyc</span>
        </div>
        <table className="invoice-meta">
          <tbody>
            <tr>
              <th>Invoice #</th>
              <th>Date</th>
            </tr>
            <tr>
              <td>{invoice.invoiceNo}</td>
              <td>{formatDate(invoice.date)}</td>
            </tr>
            <tr>
              <th>Ship Method</th>
              <td>{invoice.shipMethod}</td>
            </tr>
          </tbody>
        </table>
      </header>

      <div className="invoice-parties">
        <AddressBlock title="From" address={invoice.from} />
        <AddressBlock title="To" address={invoice.to} />
      </div>

      <table className="invoice-items">
        <thead>
          <tr>
            <th>Serial #</th>
            <th>Description</th>
            <th>Quantity</th>
            <th>Price</th>
          </tr>
        </thead>
        <tbody>
          {(invoice.items || []).map((item, index) => (
            <tr key={`${item.id}-${item.serial || index}`}>
              <td>{item.serial}</td>
              <td>{item.description}</td>
              <td>{formatNumber(item.quantity)}</td>
              <td>{invoiceAmount(item.price)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan="2">Total</td>
            <td>{formatNumber(totalQty)}</td>
            <td>{invoiceAmount(totalPrice)}</td>
          </tr>
          <tr>
            <td colSpan="4">Prepared By: {invoice.preparedBy || '-'}</td>
          </tr>
        </tfoot>
      </table>

      {(invoice.comment || invoice.memo) && (
        <div className="transfer-invoice-memo">
          <strong>Comment:</strong> {invoice.comment || invoice.memo}
        </div>
      )}
    </article>
  );
};

const groupReceiveItems = (items = []) => {
  const groups = new Map();

  items.forEach((item, index) => {
    const key = [
      item.lineNo,
      item.itemId,
      item.item,
      item.description,
      item.vendorPart,
      item.uom,
      item.poNo,
    ].join('|');
    const current = groups.get(key) || {
      ...item,
      id: `group-${item.lineNo || index}-${item.itemId || item.item || index}`,
      quantity: 0,
      totalCost: 0,
      serials: [],
    };
    const serial = String(item.serial || '').trim();

    current.quantity += Number(item.quantity) || 0;
    current.totalCost += Number(item.totalCost) || 0;
    current.ordered = item.ordered;
    current.backOrder = item.backOrder;
    current.costPer = Number(item.costPer) || current.costPer || 0;
    if (serial && !current.serials.includes(serial)) {
      current.serials.push(serial);
    }
    groups.set(key, current);
  });

  return Array.from(groups.values());
};

const receiveDocToEmailInvoice = (receiveDoc, businessAddress) => {
  if (!receiveDoc) return null;
  const groupedItems = groupReceiveItems(receiveDoc.items || []);
  return {
    id: receiveDoc.docNo,
    invoiceNo: receiveDoc.docNo,
    date: receiveDoc.receiveDate,
    type: 'Receive Items',
    shipMethod: '',
    packageCount: groupedItems.length,
    memo: receiveDoc.memo || '',
    preparedBy: receiveDoc.preparedBy || '',
    from: businessAddress,
    businessAddress,
    to: {
      name: receiveDoc.receiveLocation || receiveDoc.vendor || '',
      email: receiveDoc.receiveEmail || receiveDoc.email || '',
    },
    items: groupedItems.map((item) => {
      const serials = item.serials?.length ? item.serials : [item.serial].filter(Boolean);
      const quantity = Math.abs(Number(item.quantity) || 0) || serials.length || 1;
      return {
        id: item.id,
        serial: serials.join(', '),
        description: item.item || item.description || '',
        quantity,
        price: Number(item.costPer) || 0,
      };
    }),
  };
};

const currentYear = new Date().getFullYear();

function LocationsPage() {
  const { user } = useAuth();
  const canCreateLocation = can(user, 'locations.create');
  const canEditLocation = can(user, 'locations.edit');
  const canActivateLocation = can(user, 'locations.activate');
  const canDeactivateLocation = can(user, 'locations.deactivate');
  const canDeleteLocation = can(user, 'locations.delete');
  const [locations, setLocations] = useState([]);
  const businessAddress = findBusinessAddress(locations);
  const [filteredLocations, setFilteredLocations] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [updateLocation, setUpdateLocation] = useState(null);
  const [newLocation, setNewLocation] = useState(emptyLocation);
  const [form, setForm] = useState({ name: '' });
  const [activeTab, setActiveTab] = useState('stock');
  const [stockCheckReport, setStockCheckReport] = useState([]);
  const [stockCheckLoading, setStockCheckLoading] = useState(false);
  const [stockCheckError, setStockCheckError] = useState('');
  const [paidItemsReport, setPaidItemsReport] = useState([]);
  const [paidItemsLoading, setPaidItemsLoading] = useState(false);
  const [paidItemsError, setPaidItemsError] = useState('');
  const [paidReportYears, setPaidReportYears] = useState([currentYear]);
  const [paidReportYear, setPaidReportYear] = useState(currentYear);
  const [paidDocSearch, setPaidDocSearch] = useState('');
  const [docSearchInput, setDocSearchInput] = useState('');
  const [docSearchRows, setDocSearchRows] = useState([]);
  const [isLocationTypeaheadOpen, setIsLocationTypeaheadOpen] = useState(false);
  const [activeLocationTypeaheadIndex, setActiveLocationTypeaheadIndex] = useState(0);
  const [documentModal, setDocumentModal] = useState({
    open: false,
    type: '',
    title: '',
    loading: false,
    error: '',
    data: null,
  });
  const [invoiceEmailLoading, setInvoiceEmailLoading] = useState(false);
  const [stockCheckEmailLoading, setStockCheckEmailLoading] = useState(false);
  const [locationsReportLoading, setLocationsReportLoading] = useState(false);
  const [locationsReportEmailLoading, setLocationsReportEmailLoading] = useState(false);
  const [locationsReportModal, setLocationsReportModal] = useState({
    open: false,
    rows: [],
    loading: false,
    error: '',
  });
  const [showInactive, setShowInactive] = useState(false);
  const [locationAddStatus, setLocationAddStatus] = useState('');
  const [isAddLocationOpen, setIsAddLocationOpen] = useState(false);

  const [confirmState, setConfirmState] = useState({
    open: false,
    message: '',
    onConfirm: () => {},
  });
  const [alertState, setAlertState] = useState({ open: false, message: '' });

  const showAlert = useCallback((message) => setAlertState({ open: true, message }), []);
  const selectedLocationUpdatePayload = useMemo(
    () => (selectedLocation && updateLocation ? buildLocationUpdatePayload(selectedLocation, updateLocation) : {}),
    [selectedLocation, updateLocation]
  );
  const selectedLocationChangedFields = Object.keys(selectedLocationUpdatePayload);
  const isSelectedLocationActiveOnlyChange = selectedLocationChangedFields.length === 1
    && selectedLocationChangedFields[0] === 'active';
  const canToggleSelectedLocationActive = selectedLocation
    ? (Boolean(selectedLocation.active) ? canDeactivateLocation : canActivateLocation)
    : false;
  const canSaveSelectedLocation = selectedLocationChangedFields.length === 0
    || canEditLocation
    || (
      isSelectedLocationActiveOnlyChange
      && (Boolean(selectedLocationUpdatePayload.active) ? canActivateLocation : canDeactivateLocation)
    );

  const askConfirm = (message, onConfirm) => {
    setConfirmState({
      open: true,
      message,
      onConfirm: () => {
        onConfirm();
        setConfirmState({ open: false, message: '', onConfirm: () => {} });
      },
    });
  };

  const closeAddLocationModal = () => {
    setIsAddLocationOpen(false);
    setLocationAddStatus('');
    setNewLocation(emptyLocation);
  };

  const loadLocations = useCallback(async () => {
    try {
      const res = await api.get('/locations');
      setLocations(res.data || []);
    } catch (err) {
      console.error('Failed to load locations:', err);
      showAlert(err.response?.data?.error || err.message || 'Failed to load locations.');
    }
  }, [showAlert]);

  const loadStockCheckReport = async (location) => {
    if (!location?.id) return;

    setStockCheckReport([]);
    setStockCheckLoading(true);
    setStockCheckError('');

    try {
      const res = await api.get(`/locations/${location.id}/location-stock-check`);
      setStockCheckReport(res.data || []);
    } catch (err) {
      console.error('Failed to load location stock check:', err);
      setStockCheckError('Failed to load location stock check.');
    } finally {
      setStockCheckLoading(false);
    }
  };

  const loadPaidItemsReport = async (location, year = paidReportYear, doc = paidDocSearch) => {
    if (!location?.id) return;

    setPaidItemsReport([]);
    setPaidItemsLoading(true);
    setPaidItemsError('');

    try {
      const res = await api.get(`/locations/${location.id}/paid-items-report`, {
        params: { year, doc: doc.trim() || undefined },
      });
      setPaidItemsReport(res.data || []);
    } catch (err) {
      console.error('Failed to load paid items report:', err);
      setPaidItemsError('Failed to load paid items report.');
    } finally {
      setPaidItemsLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Locations - Services Irene's Wigs.com";
    loadLocations();
  }, [loadLocations]);

  const selectLocation = async (location) => {
    setSelectedLocation(location);
    setUpdateLocation({ ...location });
    setActiveTab('stock');
    setPaidReportYear(currentYear);
    setPaidDocSearch('');
    await loadStockCheckReport(location);
  };

  const selectLocationFromDocSearch = (location, rows = docSearchRows) => {
    const locationRows = rows.filter(row => Number(row.fromLocationId) === Number(location.id));
    const years = Array.from(new Set(locationRows.map(row => new Date(row.invoiceDate).getFullYear())))
      .filter(Number.isFinite)
      .sort((a, b) => b - a);

    setSelectedLocation(location);
    setUpdateLocation({ ...location });
    setActiveTab('stock');
    setPaidDocSearch(docSearchInput.trim());
    setPaidReportYears(years.length ? years : [currentYear]);
    setPaidReportYear(years[0] || currentYear);
    setPaidItemsReport(locationRows);
    setPaidItemsError('');
    loadStockCheckReport(location);
  };

  const handleLocationClick = (e, location) => {
    if (
      e.target.tagName === 'SELECT' ||
      e.target.tagName === 'INPUT' ||
      e.target.tagName === 'TEXTAREA' ||
      e.target.closest('select') ||
      e.target.closest('input') ||
      e.target.closest('button')
    ) {
      return;
    }
    if (docSearchRows.length > 0 && paidDocSearch.trim()) {
      selectLocationFromDocSearch(location);
    } else {
      selectLocation(location);
    }
  };

  const handleAddLocation = async (e) => {
    e.preventDefault();
    setLocationAddStatus('');
    if (!canCreateLocation) {
      setLocationAddStatus('You do not have permission to create locations.');
      return;
    }

    try {
      const res = await api.post('/locations', newLocation);
      if (res.status === 201) {
        setLocationAddStatus('Location added.');
        setNewLocation(emptyLocation);
        setIsAddLocationOpen(false);
        showAlert('Location added.');
        await loadLocations();
      }
    } catch (err) {
      setLocationAddStatus(`Error: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleSave = async () => {
    if (!selectedLocation || !updateLocation) return;

    try {
      const updatePayload = selectedLocationUpdatePayload;
      if (Object.keys(updatePayload).length === 0) {
        showAlert('No location changes to save.');
        return;
      }
      if (!canSaveSelectedLocation) {
        showAlert('You do not have permission to save those location changes.');
        return;
      }

      await api.put(`/locations/${selectedLocation.id}`, updatePayload);
      showAlert('Location updated.');
      await loadLocations();
    } catch (err) {
      console.error('Failed to update location:', err);
      showAlert(err.response?.data?.error || 'Failed to update location.');
    }
  };

  const handleDelete = async (id) => {
    if (!canDeleteLocation) {
      showAlert('You do not have permission to delete locations.');
      return;
    }
    askConfirm('Mark this location inactive?', async () => {
      try {
        await api.delete(`/locations/${id}`);
        await loadLocations();
        setSelectedLocation(null);
        setUpdateLocation(null);
      } catch (err) {
        console.error('Failed to delete location:', err);
        showAlert('Failed to delete location.');
      }
    });
  };

  const clearFilter = () => {
    setFilteredLocations(null);
    setSelectedLocation(null);
    setUpdateLocation(null);
    setForm({ name: '' });
    setDocSearchInput('');
    setDocSearchRows([]);
    setPaidDocSearch('');
    setIsLocationTypeaheadOpen(false);
    setActiveLocationTypeaheadIndex(0);
  };

  const closeDocumentModal = () => {
    setDocumentModal({
      open: false,
      type: '',
      title: '',
      loading: false,
      error: '',
      data: null,
    });
  };

  const openInvoiceModal = async (invoiceNo) => {
    if (!invoiceNo) return;
    setDocumentModal({
      open: true,
      type: 'invoice',
      title: `Invoice #${invoiceNo}`,
      loading: true,
      error: '',
      data: null,
    });

    try {
      const res = await api.get(`/invoices/${invoiceNo}`);
      setDocumentModal({
        open: true,
        type: 'invoice',
        title: `Invoice #${invoiceNo}`,
        loading: false,
        error: '',
        data: res.data,
      });
    } catch (err) {
      console.error('Failed to load invoice:', err);
      setDocumentModal((prev) => ({
        ...prev,
        loading: false,
        error: 'Failed to load invoice.',
      }));
    }
  };

  const printInvoiceDocument = () => {
    const source = document.querySelector('.location-invoice-modal .invoice-sheet');
    if (!source || !documentModal.data) return;

    const printNode = source.cloneNode(true);
    printNode.classList.add('invoice-sheet-modal');

    const iframe = document.createElement('iframe');
    iframe.title = 'Location invoice print preview';
    iframe.style.position = 'fixed';
    iframe.style.left = '-10000px';
    iframe.style.top = '0';
    iframe.style.width = '850px';
    iframe.style.height = '1100px';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const cleanup = () => {
      setTimeout(() => iframe.remove(), 250);
    };

    const runPrint = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.addEventListener('afterprint', cleanup, { once: true });
      iframe.contentWindow.print();
      setTimeout(cleanup, 1000);
    };

    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(`
      <!doctype html>
      <html>
        <head>
          <title>Invoice ${escapeHtml(documentModal.data.invoiceNo || '')}</title>
          <style>
            @page { size: portrait; margin: 10mm; }
            * { box-sizing: border-box; }
            html,
            body {
              background: #fff;
              color: #1f1f1f;
              font-family: Arial, Helvetica, sans-serif;
              margin: 0;
              min-height: 0;
            }
            .invoice-sheet {
              background: #fff;
              break-after: auto;
              box-shadow: none;
              color: #1f1f1f;
              font-family: Arial, Helvetica, sans-serif;
              margin: 0 auto;
              min-height: auto;
              padding: 44px 44px 64px;
              page-break-after: auto;
              width: 100%;
            }
            .invoice-header {
              align-items: start;
              display: grid;
              gap: 30px;
              grid-template-columns: 1fr 1fr 280px;
              margin-bottom: 72px;
            }
            .invoice-header h1 {
              letter-spacing: 0;
              margin: 10px 0 0;
              font-size: 28px;
            }
            .invoice-brand {
              color: #5f6368;
              font-family: Georgia, serif;
              font-size: 34px;
              letter-spacing: 3px;
              line-height: 1;
              margin-top: 34px;
              text-align: center;
            }
            .invoice-brand span {
              display: block;
              font-family: Arial, Helvetica, sans-serif;
              font-size: 24px;
              font-weight: 700;
              letter-spacing: 0;
              margin-top: 4px;
            }
            .invoice-meta {
              border-collapse: collapse;
              font-size: 16px;
              text-align: center;
              width: 100%;
            }
            .invoice-meta th,
            .invoice-meta td {
              border: 1px solid #444;
              font-weight: 500;
              padding: 7px 9px;
            }
            .invoice-meta th {
              background: #efefef;
              text-align: center;
            }
            .invoice-parties {
              display: grid;
              gap: 120px;
              grid-template-columns: 1fr 1fr;
              margin-bottom: 12px;
            }
            .invoice-address {
              border: 1px solid #555;
              min-height: 158px;
            }
            .invoice-address-title {
              background: #f2f2f2;
              border-bottom: 1px solid #555;
              padding: 7px 9px;
            }
            .invoice-address-body {
              display: grid;
              gap: 5px;
              padding: 11px 12px 14px;
            }
            .invoice-city-line {
              display: grid;
              grid-template-columns: 1fr auto auto;
              gap: 34px;
            }
            .invoice-items {
              border-collapse: collapse;
              break-inside: auto;
              table-layout: fixed;
              width: 100%;
            }
            .invoice-items thead {
              display: table-header-group;
            }
            .invoice-items tr {
              break-inside: avoid;
              page-break-inside: avoid;
            }
            .invoice-items th {
              background: #efefef;
              border: 1px solid #555;
              font-size: 16px;
              font-weight: 500;
              padding: 7px 9px;
              text-align: center;
            }
            .invoice-items td {
              border: 0;
              font-size: 16px;
              padding: 10px 9px;
              vertical-align: top;
            }
            .invoice-items td:nth-child(3),
            .invoice-items td:nth-child(4) {
              text-align: right;
            }
            .invoice-items tfoot td {
              border-top: 2px solid #555;
              font-weight: 700;
              padding: 9px;
            }
            .invoice-items tfoot tr:last-child td {
              border-top: 2px solid #555;
              text-align: right;
            }
            .invoice-items th:first-child,
            .invoice-items td:first-child { width: 30%; }
            .invoice-items th:nth-child(2),
            .invoice-items td:nth-child(2) { width: 42%; }
            .invoice-items th:nth-child(3),
            .invoice-items td:nth-child(3) { width: 14%; }
            .invoice-items th:nth-child(4),
            .invoice-items td:nth-child(4) { width: 14%; }
          </style>
        </head>
        <body>${printNode.outerHTML}</body>
      </html>
    `);
    doc.close();
    setTimeout(runPrint, 100);
  };

  const printReceiveDocument = () => {
    const source = document.querySelector('.location-receive-modal .transfer-workspace');
    if (!source || !documentModal.data) return;

    const printNode = source.cloneNode(true);
    printNode.querySelectorAll('.transfer-print-button, .transfer-modal-action-row').forEach((node) => node.remove());
    printNode.querySelectorAll('input').forEach((input) => {
      if (input.type === 'checkbox') {
        if (input.checked) {
          input.setAttribute('checked', 'checked');
        } else {
          input.removeAttribute('checked');
        }
      } else {
        input.setAttribute('value', input.value || '');
      }
    });

    const iframe = document.createElement('iframe');
    iframe.title = 'Receive items print preview';
    iframe.style.position = 'fixed';
    iframe.style.left = '-10000px';
    iframe.style.top = '0';
    iframe.style.width = '1122px';
    iframe.style.height = '793px';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const cleanup = () => {
      setTimeout(() => iframe.remove(), 250);
    };

    const runPrint = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.addEventListener('afterprint', cleanup, { once: true });
      iframe.contentWindow.print();
      setTimeout(cleanup, 1000);
    };

    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(`
      <!doctype html>
      <html>
        <head>
          <title>Receive Doc ${escapeHtml(documentModal.data.docNo || '')}</title>
          <style>
            @page { size: landscape; margin: 10mm; }
            * { box-sizing: border-box; }
            body { color: #2b2b2b; font-family: Arial, sans-serif; margin: 0; }
            .transfer-workspace { background: #fff; border: 0; box-shadow: none; padding: 0; width: 100%; }
            .transfer-header { align-items: start; display: grid; gap: 1rem; grid-template-columns: minmax(360px, 1fr) minmax(280px, 360px); margin-bottom: 1rem; }
            .transfer-field-group { display: grid; gap: 0.75rem; grid-template-columns: repeat(3, minmax(140px, 1fr)); }
            .transfer-doc-card { padding: 1rem; position: relative; }
            .transfer-doc-card h2 { margin: 0 0 0.75rem; }
            .transfer-doc-grid { display: grid; gap: 0.75rem; grid-template-columns: 1fr 1fr; }
            .transfer-note { grid-column: 1 / -1; opacity: 0.75; }
            label { display: flex; flex-direction: column; gap: 0.25rem; font-weight: 700; }
            input { border: 1px solid #ccc; border-radius: 4px; font: inherit; padding: 0.5rem; width: 100%; }
            .transfer-check { align-items: center; flex-direction: row; font-weight: 500; }
            .transfer-check input { width: auto; }
            .transfer-meta-row { display: grid; gap: 0.75rem; grid-template-columns: repeat(4, minmax(140px, 1fr)); margin-bottom: 1rem; }
            .transfer-table-wrap { overflow: visible; }
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ccc; padding: 0.45rem; text-align: left; vertical-align: top; }
            th { background: #e5e5e5; font-weight: 700; }
            td:nth-child(1), td:nth-child(6), td:nth-child(7), td:nth-child(8), td:nth-child(10), td:nth-child(11), td:nth-child(12) { text-align: right; }
            .transfer-footer { display: flex; font-size: 1rem; gap: 1.5rem; justify-content: flex-end; padding-top: 1rem; }
          </style>
        </head>
        <body>${printNode.outerHTML}</body>
      </html>
    `);
    doc.close();
    setTimeout(runPrint, 100);
  };

  const openReceiveDocModal = async (docNo) => {
    if (!docNo) return;
    setDocumentModal({
      open: true,
      type: 'receive',
      title: `Receive Doc #${docNo}`,
      loading: true,
      error: '',
      data: null,
    });

    try {
      const res = await api.get(`/transfers/${docNo}`);
      setDocumentModal({
        open: true,
        type: 'receive',
        title: `Receive Doc #${docNo}`,
        loading: false,
        error: '',
        data: res.data,
      });
    } catch (err) {
      console.error('Failed to load receive doc:', err);
      setDocumentModal((prev) => ({
        ...prev,
        loading: false,
        error: 'Failed to load receive doc.',
      }));
    }
  };

  const findLocation = (nameOverride) => {
    const searchText = typeof nameOverride === 'string' ? nameOverride : form.name;
    const q = searchText.trim().toLowerCase();
    if (!q) return showAlert('Please enter a location name, city, address, or phone.');

    const matches = locations.filter((location) => {
      if (!showInactive && !location.active) return false;
      const haystack = [
        location.name,
        location.address1,
        location.city,
        location.state,
        location.zip,
        location.phone,
      ].join(' ').toLowerCase();
      return haystack.includes(q);
    });

    setFilteredLocations(matches);
    if (matches.length === 1) selectLocation(matches[0]);
    if (matches.length === 0) {
      setSelectedLocation(null);
      setUpdateLocation(null);
      showAlert('No location found.');
    }
  };

  const selectLocationTypeaheadOption = (location) => {
    setForm({ name: location.name || '' });
    setFilteredLocations([location]);
    selectLocation(location);
    setIsLocationTypeaheadOpen(false);
    setActiveLocationTypeaheadIndex(0);
  };

  const locationTypeaheadQuery = form.name.trim().toLowerCase();
  const locationTypeaheadOptions = locations
    .filter((location) => {
      if (!showInactive && !location.active) return false;
      const haystack = [
        location.name,
        location.address1,
        location.city,
        location.state,
        location.zip,
        location.phone,
      ].join(' ').toLowerCase();
      return !locationTypeaheadQuery || haystack.includes(locationTypeaheadQuery);
    })
    .slice(0, 30);

  const handleLocationTypeaheadKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIsLocationTypeaheadOpen(true);
      setActiveLocationTypeaheadIndex((current) => (
        locationTypeaheadOptions.length
          ? Math.min(current + 1, locationTypeaheadOptions.length - 1)
          : 0
      ));
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIsLocationTypeaheadOpen(true);
      setActiveLocationTypeaheadIndex((current) => Math.max(current - 1, 0));
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      setIsLocationTypeaheadOpen(false);
      setActiveLocationTypeaheadIndex(0);
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      if (isLocationTypeaheadOpen && locationTypeaheadOptions[activeLocationTypeaheadIndex]) {
        const location = locationTypeaheadOptions[activeLocationTypeaheadIndex];
        const locationName = location.name || '';
        setForm({ name: locationName });
        findLocation(locationName);
        setIsLocationTypeaheadOpen(false);
        setActiveLocationTypeaheadIndex(0);
        return;
      }
      findLocation();
      setIsLocationTypeaheadOpen(false);
      return;
    }

    if (e.key === 'Tab' && isLocationTypeaheadOpen && locationTypeaheadOptions[activeLocationTypeaheadIndex]) {
      selectLocationTypeaheadOption(locationTypeaheadOptions[activeLocationTypeaheadIndex]);
    }
  };

  const listToRender = useMemo(() => {
    const base = filteredLocations === null ? locations : filteredLocations;
    if (docSearchRows.length > 0 && paidDocSearch.trim()) return base;
    return showInactive ? base : base.filter(location => location.active);
  }, [docSearchRows.length, filteredLocations, locations, paidDocSearch, showInactive]);

  const stockCheckTotals = stockCheckReport.reduce(
    (totals, row) => ({
      quantity: totals.quantity + Number(row.quantity || 0),
      retail: totals.retail + Number(row.total ?? row.retail_value ?? 0),
    }),
    { quantity: 0, retail: 0 }
  );

  const paidItemsTotals = paidItemsReport.reduce(
    (totals, row) => ({
      lines: totals.lines + 1,
      quantity: totals.quantity + Number(row.quantity || 0),
      total: totals.total + Number(row.total || 0),
    }),
    { lines: 0, quantity: 0, total: 0 }
  );

  const locationStockEmailReport = selectedLocation ? {
    locationName: selectedLocation.name || '',
    locationEmail: selectedLocation.email || '',
  } : null;

  const openLocationsReportModal = async () => {
    setLocationsReportLoading(true);
    setLocationsReportModal({ open: true, rows: [], loading: true, error: '' });
    try {
      const response = await api.get('/locations/report');
      setLocationsReportModal({ open: true, rows: response.data || [], loading: false, error: '' });
    } catch (err) {
      console.error('Failed to load locations report:', err);
      setLocationsReportModal({
        open: true,
        rows: [],
        loading: false,
        error: err.response?.data?.error || 'Failed to load locations report.',
      });
    } finally {
      setLocationsReportLoading(false);
    }
  };

  const closeLocationsReportModal = () => {
    if (locationsReportEmailLoading || locationsReportModal.loading) return;
    setLocationsReportModal({ open: false, rows: [], loading: false, error: '' });
  };

  const handlePrintLocationsReport = () => {
    try {
      const reportLocations = locationsReportModal.rows || [];
      const reportDate = new Date().toLocaleDateString('en-US');
      const rowsPerPage = 76;
      const reportPages = [];
      for (let index = 0; index < reportLocations.length; index += rowsPerPage) {
        reportPages.push(reportLocations.slice(index, index + rowsPerPage));
      }
      if (reportPages.length === 0) reportPages.push([]);

      const pagesHtml = reportPages.map((pageRows, pageIndex) => {
        const rowsHtml = pageRows.length > 0
          ? pageRows.map((location) => `
            <tr>
              <td>${escapeHtml(location.name || '')}</td>
              <td>${escapeHtml(location.address1 || '')}</td>
              <td>${escapeHtml(location.address2 || '')}</td>
              <td>${escapeHtml(location.address3 || location.phone || '')}</td>
              <td>${escapeHtml(location.city || '')}</td>
              <td>${escapeHtml(location.state || '')}</td>
              <td>${escapeHtml(location.zip || '')}</td>
              <td>${location.active ? 'Yes' : 'No'}</td>
              <td>${location.available ? 'Yes' : 'No'}</td>
            </tr>
          `).join('')
          : '<tr><td colspan="9" class="empty">No active customer locations found.</td></tr>';

        return `
          <section class="report-page">
            <header>
              <h1>Irenes Wigs | New York</h1>
              <h2>Location List</h2>
            </header>
            <table>
              <thead>
                <tr>
                  <th>Location</th>
                  <th>Address 1</th>
                  <th>Address 2</th>
                  <th>Address 3</th>
                  <th>City</th>
                  <th>State</th>
                  <th>Zip</th>
                  <th>Active</th>
                  <th>Available</th>
                </tr>
              </thead>
              <tbody>${rowsHtml}</tbody>
            </table>
            <footer>
              <span>${escapeHtml(reportDate)}</span>
              <span>Page ${pageIndex + 1} of ${reportPages.length}</span>
            </footer>
          </section>
        `;
      }).join('');

      const iframe = document.createElement('iframe');
      iframe.title = 'Locations report print preview';
      iframe.style.position = 'fixed';
      iframe.style.left = '-10000px';
      iframe.style.top = '0';
      iframe.style.width = '900px';
      iframe.style.height = '1100px';
      iframe.style.border = '0';
      document.body.appendChild(iframe);

      const cleanup = () => {
        setTimeout(() => iframe.remove(), 250);
      };
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      doc.open();
      doc.write(`
        <!doctype html>
        <html>
          <head>
            <title>Locations Report</title>
            <style>
              @page { size: portrait; margin: 0.28in; }
              * { box-sizing: border-box; }
              body { color: #111; font-family: Arial, Helvetica, sans-serif; margin: 0; }
              .report-page { break-after: page; display: flex; flex-direction: column; min-height: 10.44in; page-break-after: always; }
              .report-page:last-child { break-after: auto; page-break-after: auto; }
              header { margin: 0 0 16px; text-align: center; }
              h1 { font-size: 19px; margin: 0 0 3px; }
              h2 { font-size: 17px; margin: 0; }
              table { border-collapse: collapse; table-layout: fixed; width: 100%; }
              thead { display: table-header-group; }
              tr { break-inside: avoid; page-break-inside: avoid; }
              th { background: #e8e8e8; border: 1px solid #111; font-size: 7px; padding: 2px; text-align: left; }
              td { font-size: 6.5px; line-height: 1.12; overflow-wrap: anywhere; padding: 1.5px 2px; vertical-align: top; }
              th:nth-child(1) { width: 17%; }
              th:nth-child(2) { width: 16%; }
              th:nth-child(3) { width: 13%; }
              th:nth-child(4) { width: 14%; }
              th:nth-child(5) { width: 13%; }
              th:nth-child(6) { width: 6%; }
              th:nth-child(7) { width: 9%; }
              th:nth-child(8), th:nth-child(9) { width: 5%; text-align: center; }
              td:nth-child(8), td:nth-child(9) { text-align: center; }
              .empty { padding: 18px; text-align: center; }
              footer { display: flex; font-size: 9px; justify-content: space-between; margin-top: auto; padding-top: 10px; }
            </style>
          </head>
          <body>${pagesHtml}</body>
        </html>
      `);
      doc.close();

      const runPrint = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.addEventListener('afterprint', cleanup, { once: true });
        iframe.contentWindow.print();
        setTimeout(cleanup, 1000);
      };
      setTimeout(runPrint, 100);
    } catch (err) {
      console.error('Failed to print locations report:', err);
      showAlert(err.response?.data?.error || 'Failed to print locations report.');
    }
  };

  const handlePrintLocationStockCheck = () => {
    if (!selectedLocation || stockCheckReport.length === 0) return;

    const rowsHtml = stockCheckReport.map((row) => `
      <tr>
        <td>${escapeHtml(formatDate(row.invoiceDate))}</td>
        <td>${escapeHtml(row.invoiceNo || '')}</td>
        <td>${escapeHtml(row.serialNo || stockSerialsText(row))}</td>
        <td>${escapeHtml(row.itemService || '')}</td>
        <td>${escapeHtml(money(row.pricePer ?? row.total ?? row.retail_value))}</td>
      </tr>
    `).join('');

    const iframe = document.createElement('iframe');
    iframe.title = 'Location stock check print preview';
    iframe.style.position = 'fixed';
    iframe.style.left = '-10000px';
    iframe.style.top = '0';
    iframe.style.width = '1100px';
    iframe.style.height = '800px';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const cleanup = () => {
      setTimeout(() => iframe.remove(), 250);
    };

    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(`
      <!doctype html>
      <html>
        <head>
          <title>Location Stock Check - ${escapeHtml(selectedLocation.name || '')}</title>
          <style>
            @page { size: portrait; margin: 0.35in; }
            * { box-sizing: border-box; }
            body { color: #111; font-family: Arial, Helvetica, sans-serif; margin: 0; }
            .report-header { margin-bottom: 12px; text-align: center; }
            .brand { color: #555; font-family: Georgia, serif; font-size: 24px; letter-spacing: 2px; line-height: 1; }
            .brand span { display: block; font-family: Arial, Helvetica, sans-serif; font-size: 13px; font-weight: 700; letter-spacing: 0; margin-top: 1px; }
            h1 { font-size: 17px; margin: 8px 0 0; }
            h2 { font-size: 18px; margin: 2px 0 8px; }
            .report-location { border: 1.5px solid #111; font-size: 13px; padding: 3px 7px; text-align: left; text-transform: uppercase; }
            .totals { display: flex; gap: 18px; justify-content: flex-end; margin-bottom: 8px; font-size: 12px; }
            table { border-collapse: collapse; font-size: 11px; width: 100%; }
            th, td { border: 0; border-bottom: 1px solid #d0d0d0; padding: 3px 5px; text-align: left; vertical-align: top; }
            th { font-weight: 700; }
            td:nth-child(3) { text-align: center; }
            td:nth-child(5) { text-align: right; }
            td:nth-child(4) { overflow-wrap: anywhere; }
            tfoot td { border-top: 1.5px solid #111; font-weight: 700; }
          </style>
        </head>
        <body>
          <header class="report-header">
            <div class="brand">IRENE'S<span>nyc</span></div>
            <h1>Irenes Wigs | New York</h1>
            <h2>Location Collection Report</h2>
            <div class="report-location">${escapeHtml(selectedLocation.name || '')}</div>
          </header>
          <div class="totals">
            <strong>${escapeHtml(formatNumber(stockCheckTotals.quantity))} pieces</strong>
            <strong>${escapeHtml(money(stockCheckTotals.retail))} value</strong>
          </div>
          <table>
            <thead>
              <tr>
                <th>Invoice Date</th>
                <th>Invoice No</th>
                <th>SN</th>
                <th>Item</th>
                <th>Price</th>
              </tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
            <tfoot>
              <tr>
                <td colspan="4">Total</td>
                <td>${escapeHtml(money(stockCheckTotals.retail))}</td>
              </tr>
            </tfoot>
          </table>
        </body>
      </html>
    `);
    doc.close();

    const runPrint = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.addEventListener('afterprint', cleanup, { once: true });
      iframe.contentWindow.print();
      setTimeout(cleanup, 1000);
    };
    setTimeout(runPrint, 100);
  };

  return (
    <>
      <TopNav />
      <div className="inventory-container">
        <div className="customers-page-header">
          <h2>Locations</h2>
          <div className="customers-heading-tools">
            <button
              type="button"
              className="button small blue"
              onClick={openLocationsReportModal}
              disabled={locationsReportLoading}
            >
              {locationsReportLoading ? 'Loading...' : 'Locations Report'}
            </button>
            <button
              type="button"
              className="button small blue"
              onClick={() => {
                setLocationAddStatus('');
                setNewLocation(emptyLocation);
                setIsAddLocationOpen(true);
              }}
              disabled={!canCreateLocation}
            >
              Add Location
            </button>
          </div>
        </div>

        <div className="form-group">
          <div className="input-button-group">
            <div className="location-search-filter-group">
              <div className="location-search-stack">
                <label className="checkbox-label location-show-inactive-toggle">
                  <input
                    type="checkbox"
                    checked={showInactive}
                    onChange={(e) => {
                      setShowInactive(e.target.checked);
                      setActiveLocationTypeaheadIndex(0);
                    }}
                  />
                  Show Inactive Locations
                </label>
                <div className="customer-search-typeahead">
                  <input
                    type="text"
                    placeholder="Search locations"
                    value={form.name}
                    onChange={(e) => {
                      setForm({ name: e.target.value });
                      setIsLocationTypeaheadOpen(true);
                      setActiveLocationTypeaheadIndex(0);
                    }}
                    onFocus={() => {
                      setIsLocationTypeaheadOpen(true);
                      setActiveLocationTypeaheadIndex(0);
                    }}
                    onBlur={() => setTimeout(() => setIsLocationTypeaheadOpen(false), 120)}
                    onKeyDown={handleLocationTypeaheadKeyDown}
                    className="input"
                    autoComplete="off"
                    role="combobox"
                    aria-expanded={isLocationTypeaheadOpen}
                    aria-controls="location-typeahead-menu"
                  />
                  {isLocationTypeaheadOpen && locationTypeaheadOptions.length > 0 && (
                    <div className="customer-typeahead-menu" id="location-typeahead-menu" role="listbox">
                      {locationTypeaheadOptions.map((location, index) => (
                        <button
                          type="button"
                          key={location.id}
                          className={index === activeLocationTypeaheadIndex ? 'active' : ''}
                          onMouseDown={(e) => e.preventDefault()}
                          onMouseEnter={() => setActiveLocationTypeaheadIndex(index)}
                          onClick={() => selectLocationTypeaheadOption(location)}
                          role="option"
                          aria-selected={index === activeLocationTypeaheadIndex}
                        >
                          <span>{location.name || '(No name)'}</span>
                          <small>
                            {[location.city, location.state, location.phone].filter(Boolean).join(' - ')}
                          </small>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <button type="button" onClick={clearFilter} className="button small yellow">
                Clear
              </button>
            </div>
          </div>
        </div>

        <div className="card-list employees">
          {listToRender.length === 0 && <div className="empty-state">No locations to display.</div>}

          {listToRender.map((location) => (
            <div
              key={location.id}
              className={`card ${selectedLocation?.id === location.id ? 'selected' : ''}`}
            >
              <div onClick={(e) => handleLocationClick(e, location)} style={{ cursor: 'pointer' }}>
                <h2>
                  {location.name}
                </h2>
                <p>
                  {[location.address1, location.city, location.state, location.zip]
                    .filter(Boolean)
                    .join(', ')}
                </p>

                {selectedLocation?.id === location.id && updateLocation && (
                  <div
                    className="employee-detail-panel"
                    onClick={(e) => e.stopPropagation()}
                    style={{ marginTop: 10 }}
                  >
                    <div className="customer-tabs">
                      <button
                        type="button"
                        className={activeTab === 'stock' ? 'active' : ''}
                        onClick={() => {
                          setActiveTab('stock');
                          loadStockCheckReport(selectedLocation || location);
                        }}
                      >
                        Location Stock Check
                      </button>
                      <button
                        type="button"
                        className={activeTab === 'settings' ? 'active' : ''}
                        onClick={() => setActiveTab('settings')}
                      >
                        Settings
                      </button>
                    </div>

                    <div className="customer-tab-panel inventory-form">
                      {activeTab === 'settings' && (
                        <>
                          <div className="location-settings-address-row">
                            <input
                              type="text"
                              placeholder="Location Name"
                              value={updateLocation.name || ''}
                              onChange={(e) => setUpdateLocation({ ...updateLocation, name: e.target.value })}
                              disabled={!canEditLocation}
                            />
                            <input
                              type="text"
                              placeholder="Address 1"
                              value={updateLocation.address1 || ''}
                              onChange={(e) => setUpdateLocation({ ...updateLocation, address1: e.target.value })}
                              disabled={!canEditLocation}
                            />
                            <input
                              type="text"
                              placeholder="Address 2"
                              value={updateLocation.address2 || ''}
                              onChange={(e) => setUpdateLocation({ ...updateLocation, address2: e.target.value })}
                              disabled={!canEditLocation}
                            />
                            <input
                              type="text"
                              placeholder="City"
                              value={updateLocation.city || ''}
                              onChange={(e) => setUpdateLocation({ ...updateLocation, city: e.target.value })}
                              disabled={!canEditLocation}
                            />
                            <input
                              type="text"
                              placeholder="State"
                              value={updateLocation.state || ''}
                              onChange={(e) => setUpdateLocation({ ...updateLocation, state: e.target.value })}
                              disabled={!canEditLocation}
                            />
                            <input
                              type="text"
                              placeholder="Zip"
                              value={updateLocation.zip || ''}
                              onChange={(e) => setUpdateLocation({ ...updateLocation, zip: e.target.value })}
                              disabled={!canEditLocation}
                            />
                            <input
                              type="text"
                              placeholder="Phone"
                              value={updateLocation.phone || ''}
                              onChange={(e) => setUpdateLocation({ ...updateLocation, phone: e.target.value })}
                              disabled={!canEditLocation}
                            />
                            <input
                              type="email"
                              placeholder="Email"
                              value={updateLocation.email || ''}
                              onChange={(e) => setUpdateLocation({ ...updateLocation, email: e.target.value })}
                              disabled={!canEditLocation}
                            />
                          </div>
                          <div className="location-settings-electronic-row">
                            <label>
                              <span>Website</span>
                              <input
                                type="text"
                                placeholder="Website address"
                                value={updateLocation.website || ''}
                                onChange={(e) => setUpdateLocation({ ...updateLocation, website: e.target.value })}
                                disabled={!canEditLocation}
                              />
                            </label>
                            <label>
                              <span>Instagram</span>
                              <input
                                type="text"
                                placeholder="Instagram address or handle"
                                value={updateLocation.instagram || ''}
                                onChange={(e) => setUpdateLocation({ ...updateLocation, instagram: e.target.value })}
                                disabled={!canEditLocation}
                              />
                            </label>
                            <label>
                              <span>Facebook</span>
                              <input
                                type="text"
                                placeholder="Facebook address"
                                value={updateLocation.facebook || ''}
                                onChange={(e) => setUpdateLocation({ ...updateLocation, facebook: e.target.value })}
                                disabled={!canEditLocation}
                              />
                            </label>
                            <label>
                              <span>Custom Platform</span>
                              <input
                                type="text"
                                placeholder="Platform name"
                                value={updateLocation.customPlatform || ''}
                                onChange={(e) => setUpdateLocation({ ...updateLocation, customPlatform: e.target.value })}
                                disabled={!canEditLocation}
                              />
                            </label>
                            <label>
                              <span>Electronic Address</span>
                              <input
                                type="text"
                                placeholder="Custom electronic address"
                                value={updateLocation.customAddress || ''}
                                onChange={(e) => setUpdateLocation({ ...updateLocation, customAddress: e.target.value })}
                                disabled={!canEditLocation}
                              />
                            </label>
                          </div>
                          <div className="location-settings-options-row">
                            <label className="checkbox-label">
                              <input
                                type="checkbox"
                                checked={Boolean(updateLocation.active)}
                                onChange={(e) => setUpdateLocation({ ...updateLocation, active: e.target.checked })}
                                disabled={!canToggleSelectedLocationActive}
                              />
                              Active
                            </label>
                            <label className="checkbox-label">
                              <input
                                type="checkbox"
                                checked={Boolean(updateLocation.available)}
                                onChange={(e) => setUpdateLocation({ ...updateLocation, available: e.target.checked })}
                                disabled={!canEditLocation}
                              />
                              Available
                            </label>
                            <label className="checkbox-label">
                              <input
                                type="checkbox"
                                checked={Boolean(updateLocation.show_on_item_list)}
                                onChange={(e) => setUpdateLocation({ ...updateLocation, show_on_item_list: e.target.checked })}
                                disabled={!canEditLocation}
                              />
                              Show On Item List
                            </label>
                          </div>
                          <div className="location-settings-notes-row">
                            <textarea
                              placeholder="Notes"
                              value={updateLocation.notes || ''}
                              onChange={(e) => setUpdateLocation({ ...updateLocation, notes: e.target.value })}
                              disabled={!canEditLocation}
                            />
                          </div>
                          <div className="employee-controls location-settings-actions">
                            <button onClick={handleSave} className="inline-button update-button" disabled={!canSaveSelectedLocation}>
                              Save Changes
                            </button>
                            {canDeleteLocation && (
                              <button onClick={() => handleDelete(location.id)} className="inline-button delete-button">
                                Delete
                              </button>
                            )}
                          </div>
                        </>
                      )}

                      {activeTab === 'paid' && (
                        <div className="customer-tab-panel">
                          <div className="input-button-group" style={{ marginBottom: 12 }}>
                            <label className="checkbox-label">
                              Invoice Year
                              <select
                                value={paidReportYear}
                                onChange={(e) => {
                                  const year = Number(e.target.value);
                                  setPaidReportYear(year);
                                  loadPaidItemsReport(selectedLocation || location, year, paidDocSearch);
                                }}
                              >
                                {paidReportYears.map((year) => (
                                  <option key={year} value={year}>
                                    {year}
                                  </option>
                                ))}
                              </select>
                            </label>
                          </div>
                          {paidItemsLoading && <p>Loading paid items report...</p>}
                          {!paidItemsLoading && paidItemsError && <p style={{ color: 'red' }}>{paidItemsError}</p>}
                          {!paidItemsLoading && !paidItemsError && paidItemsReport.length === 0 && (
                            <p style={{ opacity: 0.7 }}>No paid items found for this location.</p>
                          )}
                          {!paidItemsLoading && !paidItemsError && paidItemsReport.length > 0 && (
                            <div style={{ overflowX: 'auto' }}>
                              <p>
                                <strong>{paidItemsTotals.lines}</strong> lines,{' '}
                                <strong>{paidItemsTotals.quantity}</strong> units,{' '}
                                <strong>{money(paidItemsTotals.total)}</strong> paid total
                              </p>
                              <table className="inventory-table location-paid-items-table">
                                <thead>
                                  <tr>
                                    <th>Paid Date</th>
                                    <th>Invoice #</th>
                                    <th>Receive Doc #</th>
                                    <th>Invoice Date</th>
                                    <th>Serial #</th>
                                    <th>Description</th>
                                    <th>Collection</th>
                                    <th>Color</th>
                                    <th>Cap Size</th>
                                    <th>Qty</th>
                                    <th>Price</th>
                                    <th>Total</th>
                                    <th>To Location</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {paidItemsReport.map((row, index) => (
                                    <tr key={`${row.invoiceNo}-${row.itemId}-${row.serial}-${index}`}>
                                      <td>{formatDate(row.paidDate)}</td>
                                      <td>
                                        {row.invoiceNo ? (
                                          <button
                                            type="button"
                                            className="location-doc-link"
                                            onClick={() => openInvoiceModal(row.invoiceNo)}
                                          >
                                            {row.invoiceNo}
                                          </button>
                                        ) : '-'}
                                      </td>
                                      <td>
                                        {row.sourceDocNo ? (
                                          <button
                                            type="button"
                                            className="location-doc-link"
                                            onClick={() => openReceiveDocModal(row.sourceDocNo)}
                                          >
                                            {row.sourceDocNo}
                                          </button>
                                        ) : '-'}
                                      </td>
                                      <td>{formatDate(row.invoiceDate)}</td>
                                      <td>{row.serial || '-'}</td>
                                      <td>{row.description || '-'}</td>
                                      <td>{row.collection || '-'}</td>
                                      <td>{row.color || '-'}</td>
                                      <td>{row.cap_size || '-'}</td>
                                      <td>{row.quantity}</td>
                                      <td>{money(row.unitPrice)}</td>
                                      <td>{money(row.total)}</td>
                                      <td>{row.toLocation || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      )}

                      {activeTab === 'stock' && (
                        <div className="customer-tab-panel">
                          {stockCheckLoading && <p>Loading location stock check...</p>}
                          {!stockCheckLoading && stockCheckError && <p style={{ color: 'red' }}>{stockCheckError}</p>}
                          {!stockCheckLoading && !stockCheckError && stockCheckReport.length === 0 && (
                            <p style={{ opacity: 0.7 }}>No stock balances found for this location.</p>
                          )}
                          {!stockCheckLoading && !stockCheckError && stockCheckReport.length > 0 && (
                            <div className="location-stock-report-wrap">
                              <div className="location-stock-toolbar">
                                <p>
                                  <strong>{formatNumber(stockCheckTotals.quantity)}</strong> pieces,{' '}
                                  <strong>{money(stockCheckTotals.retail)}</strong> value
                                </p>
                                <div className="location-stock-actions">
                                  <button
                                    type="button"
                                    className="button small blue"
                                    onClick={handlePrintLocationStockCheck}
                                  >
                                    Print
                                  </button>
                                  <LocationStockEmailActions
                                    locationId={selectedLocation?.id}
                                    report={locationStockEmailReport}
                                    disabled={!selectedLocation || stockCheckReport.length === 0}
                                    loading={stockCheckEmailLoading}
                                    buttonClassName="button small blue"
                                    onLoadingChange={setStockCheckEmailLoading}
                                    successMessage={(to) => `Location stock check emailed to ${to}.`}
                                    onError={showAlert}
                                  />
                                </div>
                              </div>
                              <div className="location-stock-report-sheet">
                                <header className="location-stock-report-header">
                                  <div className="location-stock-report-brand">
                                    IRENE'S
                                    <span>nyc</span>
                                  </div>
                                  <h3>Irenes Wigs | New York</h3>
                                  <h4>Location Collection Report</h4>
                                  <div className="location-stock-report-name">{selectedLocation?.name || ''}</div>
                                </header>
                                <table className="location-stock-check-table">
                                  <thead>
                                    <tr>
                                      <th>Invoice Date</th>
                                      <th>Invoice No</th>
                                      <th>SN</th>
                                      <th>Item</th>
                                      <th>Price</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {stockCheckReport.map((row, index) => (
                                      <tr key={`${row.invoiceNo || ''}-${row.serialNo || index}-${index}`}>
                                        <td>{formatDate(row.invoiceDate)}</td>
                                        <td>{row.invoiceNo || '-'}</td>
                                        <td>{row.serialNo || stockSerialsText(row)}</td>
                                        <td>{row.itemService || '-'}</td>
                                        <td>{money(row.pricePer ?? row.total ?? row.retail_value)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {locationsReportModal.open && (
        <div className="invoice-modal-overlay print-modal-overlay" onClick={closeLocationsReportModal}>
          <div
            className="invoice-modal print-modal locations-report-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="invoice-modal-header locations-report-modal-header">
              <h3>Locations Report</h3>
              <div className="invoice-modal-actions">
                <button
                  type="button"
                  className="button small blue"
                  onClick={handlePrintLocationsReport}
                  disabled={locationsReportModal.loading || Boolean(locationsReportModal.error)}
                >
                  Print
                </button>
                <EmailActions
                  endpoint="/locations/report/email"
                  templateKey="locationStock"
                  templateValues={{
                    document: 'Locations Report',
                    documentNumber: '',
                    recipientName: '',
                    locationName: 'All Active Locations',
                  }}
                  subject="Locations Report"
                  quickTitle="Send Locations Report"
                  quickPrompt="Send locations report to:"
                  attachmentPdf="Locations_Report.pdf"
                  attachmentExcel="Locations_Report.xls"
                  disabled={locationsReportModal.loading || Boolean(locationsReportModal.error)}
                  loading={locationsReportEmailLoading}
                  buttonClassName="button small blue"
                  successMessage={(to) => `Locations report emailed to ${to}.`}
                  onLoadingChange={setLocationsReportEmailLoading}
                  onError={showAlert}
                />
                <button
                  type="button"
                  className="button small red"
                  onClick={closeLocationsReportModal}
                  disabled={locationsReportEmailLoading || locationsReportModal.loading}
                >
                  Close
                </button>
              </div>
            </div>

            {locationsReportModal.loading && <p className="invoice-modal-status">Loading locations report...</p>}
            {!locationsReportModal.loading && locationsReportModal.error && (
              <p className="invoice-modal-status error">{locationsReportModal.error}</p>
            )}
            {!locationsReportModal.loading && !locationsReportModal.error && (
              <div className="locations-report-preview">
                <article className="locations-report-sheet">
                  <header>
                    <h1>Irenes Wigs | New York</h1>
                    <h2>Location List</h2>
                  </header>
                  <table>
                    <thead>
                      <tr>
                        <th>Location</th>
                        <th>Address 1</th>
                        <th>Address 2</th>
                        <th>Address 3</th>
                        <th>City</th>
                        <th>State</th>
                        <th>Zip</th>
                        <th>Active</th>
                        <th>Available</th>
                      </tr>
                    </thead>
                    <tbody>
                      {locationsReportModal.rows.map((location) => (
                        <tr key={location.id}>
                          <td>{location.name}</td>
                          <td>{location.address1}</td>
                          <td>{location.address2}</td>
                          <td>{location.address3 || location.phone}</td>
                          <td>{location.city}</td>
                          <td>{location.state}</td>
                          <td>{location.zip}</td>
                          <td>{location.active ? 'Yes' : 'No'}</td>
                          <td>{location.available ? 'Yes' : 'No'}</td>
                        </tr>
                      ))}
                      {locationsReportModal.rows.length === 0 && (
                        <tr>
                          <td colSpan="9" className="locations-report-empty">No active customer locations found.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  <footer>{new Date().toLocaleDateString('en-US')}</footer>
                </article>
              </div>
            )}
          </div>
        </div>
      )}

      {isAddLocationOpen && (
        <div className="transfer-confirm-overlay" onClick={closeAddLocationModal}>
          <form className="transfer-confirm-modal customer-add-modal" onSubmit={handleAddLocation} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="transfer-modal-close"
              onClick={closeAddLocationModal}
            >
              x
            </button>
            <h3>Add Location</h3>
            <label>
              <span>Location Name</span>
              <input
                className="transfer-confirm-input"
                type="text"
                value={newLocation.name}
                onChange={(e) => setNewLocation({ ...newLocation, name: e.target.value })}
                autoFocus
                required
              />
            </label>
            <label>
              <span>Address</span>
              <input
                className="transfer-confirm-input"
                type="text"
                value={newLocation.address1}
                onChange={(e) => setNewLocation({ ...newLocation, address1: e.target.value })}
              />
            </label>
            <label>
              <span>City</span>
              <input
                className="transfer-confirm-input"
                type="text"
                value={newLocation.city}
                onChange={(e) => setNewLocation({ ...newLocation, city: e.target.value })}
              />
            </label>
            <label>
              <span>State</span>
              <input
                className="transfer-confirm-input"
                type="text"
                value={newLocation.state}
                onChange={(e) => setNewLocation({ ...newLocation, state: e.target.value })}
              />
            </label>
            <label>
              <span>Phone</span>
              <input
                className="transfer-confirm-input"
                type="tel"
                value={newLocation.phone}
                onChange={(e) => setNewLocation({ ...newLocation, phone: e.target.value })}
              />
            </label>
            <label>
              <span>Website</span>
              <input
                className="transfer-confirm-input"
                type="text"
                value={newLocation.website}
                onChange={(e) => setNewLocation({ ...newLocation, website: e.target.value })}
              />
            </label>
            <label>
              <span>Instagram</span>
              <input
                className="transfer-confirm-input"
                type="text"
                placeholder="Address or handle"
                value={newLocation.instagram}
                onChange={(e) => setNewLocation({ ...newLocation, instagram: e.target.value })}
              />
            </label>
            <label>
              <span>Facebook</span>
              <input
                className="transfer-confirm-input"
                type="text"
                value={newLocation.facebook}
                onChange={(e) => setNewLocation({ ...newLocation, facebook: e.target.value })}
              />
            </label>
            <label>
              <span>Custom Platform</span>
              <input
                className="transfer-confirm-input"
                type="text"
                placeholder="Platform name"
                value={newLocation.customPlatform}
                onChange={(e) => setNewLocation({ ...newLocation, customPlatform: e.target.value })}
              />
            </label>
            <label>
              <span>Electronic Address</span>
              <input
                className="transfer-confirm-input"
                type="text"
                value={newLocation.customAddress}
                onChange={(e) => setNewLocation({ ...newLocation, customAddress: e.target.value })}
              />
            </label>
            {locationAddStatus && <p className="transfer-confirm-error">{locationAddStatus}</p>}
            <div className="transfer-confirm-actions">
              <button
                type="button"
                className="button small transfer-confirm-cancel"
                onClick={closeAddLocationModal}
              >
                Cancel
              </button>
              <button type="submit" className="button small blue" disabled={!canCreateLocation}>
                Add Location
              </button>
            </div>
          </form>
        </div>
      )}
      {alertState.open && (
        <AlertModal message={alertState.message} onClose={() => setAlertState({ open: false, message: '' })} />
      )}
      {confirmState.open && (
        <ConfirmModal
          open={confirmState.open}
          message={confirmState.message}
          onConfirm={confirmState.onConfirm}
          onCancel={() => setConfirmState({ open: false, message: '', onConfirm: () => {} })}
        />
      )}
      {documentModal.open && documentModal.type === 'invoice' && (
        <div className="invoice-modal-overlay print-modal-overlay" onClick={closeDocumentModal}>
          <div className="invoice-modal print-modal location-invoice-modal" onClick={(e) => e.stopPropagation()}>
            <div className="invoice-modal-header">
              <strong>
                {documentModal.data
                  ? `Invoice #${documentModal.data.invoiceNo}`
                  : 'Invoice'}
              </strong>
              <div className="invoice-modal-actions">
                <InvoicePrintEmailActions
                  invoice={documentModal.data}
                  disabled={!documentModal.data}
                  loading={invoiceEmailLoading}
                  onPrint={printInvoiceDocument}
                  onLoadingChange={setInvoiceEmailLoading}
                  successMessage={(to) => `Invoice #${documentModal.data?.invoiceNo || ''} emailed to ${to}.`}
                  onError={showAlert}
                />
                <button
                  type="button"
                  className="button small red"
                  onClick={closeDocumentModal}
                >
                  Close
                </button>
              </div>
            </div>

            {documentModal.loading && <p className="invoice-modal-status">Loading invoice...</p>}
            {!documentModal.loading && documentModal.error && (
              <p className="invoice-modal-status error">{documentModal.error}</p>
            )}
            {!documentModal.loading && documentModal.data && (
              <InvoicePreview invoice={documentModal.data} />
            )}
          </div>
        </div>
      )}
      {documentModal.open && documentModal.type !== 'invoice' && (
        <div className="transfer-modal-overlay" onClick={closeDocumentModal}>
          <div className="transfer-modal location-receive-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="transfer-modal-close" onClick={closeDocumentModal}>
              x
            </button>

            {documentModal.loading && <p className="transfer-status">Loading...</p>}
            {!documentModal.loading && documentModal.error && (
              <p className="transfer-status">{documentModal.error}</p>
            )}

            {!documentModal.loading && !documentModal.error && documentModal.type === 'receive' && documentModal.data && (
              <section className="inventory-card transfer-workspace">
                <div className="transfer-header">
                  <div className="transfer-field-group transfer-party-fields">
                    <label>
                      Vendor
                      <input value={documentModal.data.vendor || ''} readOnly />
                    </label>
                    <label>
                      Pay To
                      <input value={documentModal.data.payTo || ''} readOnly />
                    </label>
                    <label>
                      Receive To Location
                      <input value={documentModal.data.receiveLocation || ''} readOnly />
                    </label>
                  </div>

                  <div className="transfer-doc-card">
                    <div className="transfer-modal-action-row">
                      <button type="button" className="button small blue transfer-print-button" onClick={printReceiveDocument}>
                        Print
                      </button>
                      <InvoicePrintEmailActions
                        invoice={receiveDocToEmailInvoice(documentModal.data, businessAddress)}
                        showPrint={false}
                        loading={invoiceEmailLoading}
                        onLoadingChange={setInvoiceEmailLoading}
                        successMessage={(to) => `Receive Doc #${documentModal.data?.docNo || ''} emailed to ${to}.`}
                        onError={showAlert}
                      />
                      <button type="button" className="button small red transfer-print-button" onClick={closeDocumentModal}>
                        Close
                      </button>
                    </div>
                    <h2>Receive Items</h2>
                    <div className="transfer-doc-grid">
                      <label>
                        Doc #
                        <input value={documentModal.data.docNo || ''} readOnly />
                      </label>
                      <label>
                        Receive Date
                        <input type="date" value={documentModal.data.receiveDate ? new Date(documentModal.data.receiveDate).toISOString().slice(0, 10) : ''} readOnly />
                      </label>
                      <span className="transfer-note">Not Recorded in QuickBooks</span>
                      <label className="transfer-check">
                        <input type="checkbox" checked={Boolean(documentModal.data.received)} readOnly />
                        Received
                      </label>
                      <label className="transfer-check">
                        <input type="checkbox" checked={Boolean(documentModal.data.ncOnly)} readOnly />
                        NC Only
                      </label>
                    </div>
                  </div>
                </div>

                <div className="transfer-meta-row">
                  <label>
                    Expected Date
                    <input type="date" value={documentModal.data.expectedDate ? new Date(documentModal.data.expectedDate).toISOString().slice(0, 10) : ''} readOnly />
                  </label>
                  <label>
                    # Pckg
                    <input value={(documentModal.data.items || []).length} readOnly />
                  </label>
                </div>

                <div className="transfer-table-wrap">
                  <table className="inventory-table transfer-table">
                    <thead>
                      <tr>
                        <th>Line #</th>
                        <th>Item</th>
                        <th>Description</th>
                        <th>Serial #</th>
                        <th>Vend Part #</th>
                        <th>Qty</th>
                        <th>Ordered</th>
                        <th>Back Order</th>
                        <th>UOM</th>
                        <th>Cost Per</th>
                        <th>Total Cost</th>
                        <th>PO #</th>
                        <th>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupReceiveItems(documentModal.data.items || []).map((item) => (
                        <tr key={item.id}>
                          <td>{item.lineNo}</td>
                          <td>{item.item}</td>
                          <td>{item.description}</td>
                          <td>{serialListText(item) || '-'}</td>
                          <td>{item.vendorPart}</td>
                          <td>{formatNumber(item.quantity)}</td>
                          <td>{formatNumber(item.ordered)}</td>
                          <td>{formatNumber(item.backOrder)}</td>
                          <td>{item.uom}</td>
                          <td>{money(item.costPer)}</td>
                          <td>{money(item.totalCost)}</td>
                          <td>{item.poNo}</td>
                          <td className="transfer-details-cell">v</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="transfer-footer">
                  <span>Subtotal&nbsp; {money(documentModal.data.subtotal)}</span>
                  <span>Charges&nbsp; {money(documentModal.data.charges)}</span>
                  <strong>Total&nbsp; {money(documentModal.data.total)}</strong>
                </div>
              </section>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default LocationsPage;
